import { SceneContainer } from "GameObjects/StoryGame/SceneContainer";
import { SceneDraggable } from "GameObjects/StoryGame/SceneDraggable";
import { SceneSprite } from "GameObjects/StoryGame/SceneSprite";
import { Elastic, Expo, TimelineLite, TweenLite, TweenMax } from "gsap";
import * as HLF from "html-living-framework";
import { Application } from "Singletons/Application";
import { AbstractStoryState } from "States/AbstractStoryState";
import { list, Mix } from "Utils/Reflection";
import * as Constants from "Data/Constants";

import {ch1_p017} from "../../../layouts/ch1_p17/ch1_p017";

export class Chapter1Page17 extends AbstractStoryState {

	readonly background = this.addChild(new SceneSprite());
	readonly outside = this.addChild(new SceneSprite());
	readonly doorFrame = this.addChild(new SceneSprite());
	readonly couch = this.addChild(new SceneSprite());
	readonly outsideLight = this.addChild(new SceneSprite());
    readonly holly = this.addChild(new SceneSprite());
    readonly lights = list(()=>this.addChildAt(new SceneSprite(), 5));
	readonly hollyShadow = this.addChild(new SceneSprite());
	readonly texture = list(()=>this.addChildAt(new SceneSprite(), 11));
	readonly frame = this.addChild(new SceneSprite());
	readonly lines = this.addChild(new SceneSprite());

	dontScaleUp = true;

    public init(pSize: PIXI.Point, pData: any) {
        // exported layout from Animate CC
        ch1_p017.call(this);

		// super.init calls onResize
		super.init(pSize, pData);
	}

	public animateIn(pOnComplete: ()=>void) {
		super.animateIn(pOnComplete);

		const d = 5;
		const t = new TimelineLite();

		const ease = Expo.easeOut;

		// horizon
		const center = {x: 0.5, y: 0.75};
		const containerPos = {
			x: (Constants.SCENE_WIDTH * center.x),
			y: (Constants.SCENE_HEIGHT * center.y),
		};

		// set background, line art, and lights to same z depth
		this.outside.z = 5;
		this.outsideLight.z = 5;

		this.frame.z = 4;
		this.lines.z = 4;
		this.background.z = 4;
		this.doorFrame.z = 4;
		this.texture.forEach(tex=>tex.z = 4);
		this.lights.forEach(l=>l.z = 4);

		this.holly.z = 3;
		this.hollyShadow.z = 3;

		this.couch.z = 2;

		const childrenToAnimate = [
			this.background,
			this.outside,
			this.doorFrame,
			this.couch,
			this.outsideLight,
			this.holly,
			...this.lights,
			this.hollyShadow,
			...this.texture,
			this.frame,
			this.lines,
		]

		// parallax
		for (let i = childrenToAnimate.length-1; i >= 0; --i) {
			const siblingIndex = this.getChildIndex(childrenToAnimate[i]);
			const child = this.removeChild(childrenToAnimate[i]);
			const container = this.addChildAt(new PIXI.Container(), siblingIndex);
			container.name = child.name + "_parallax";
			container.position.set(containerPos.x, containerPos.y);
			child.setParent(container);
			HLF.PointUtils.subtractFromPoint(child.position, container.position);
			const fromScale = 1.1 + ((4-child.z) / 10); // TODO
			t.fromTo(container, d, {pixi: {scale: fromScale}}, {pixi: {scale: 1}, ease}, 0);
		};

		// slide in holly from left
		const hollyContainer = this.holly.parent;
		t.from(hollyContainer, d, {pixi: {
			x: hollyContainer.x - 250,
		}, ease}, 0);

		// fade in watercolor edges
		t.from([this.frameLeft, this.frameRight, this.lines], d, {pixi: {alpha: 0}}, 0);
	}

	public animateOut(pOnComplete: ()=>void) {
		super.animateOut(pOnComplete);
		// fade out old background music
		const prevLoop = Application.instance.audio.getAudioTrack("HH_Ch1_LoopB_MainLoop", HLF.AudioCategory.BGM);
		prevLoop?.fadeTo(0, 1000);
		prevLoop?.once(HLF.HowlerUtils.Events.FADE, ()=>{
			prevLoop.unloadSource();
		});
	}

	onAnimateInComplete() {
		super.onAnimateInComplete();
		TweenLite.delayedCall(1.5, ()=>this.gotoNextState());
	}
}